// UsersList.jsx (Responsive - stacked view for mobile with icons)
import React, { useState, useEffect, useMemo } from "react";
import SinglesUsersApi from "../../api_calls/SinglesUsersApi";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Switch,
  Grid,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PublicIcon from "@mui/icons-material/Public";
import PaymentIcon from "@mui/icons-material/Payment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";

import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../context/WebSocketContext";

const iconSx = { verticalAlign: "middle", mr: 1, fontSize: 18 };

const capitalize = (text) =>
  text
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { sendMessage } = useWebSocket();
  const { user } = useAuth();
  const userId = user?.user_id || "";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const fetchedData = await SinglesUsersApi.fetchSingles();

        if (fetchedData.status !== "success") {
          throw new Error(`API error! status: ${fetchedData.status}`);
        }

        const usersWithStatus = fetchedData.data.map((user) => ({
          ...user,
          isActive: user.status === "active",
        }));
        setUsers(usersWithStatus);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleToggleActive = (id) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === id ? { ...user, isActive: !user.isActive } : user
      )
    );
    sendMessage({
      user_id: userId,
      tag: "toggle_status",
      payload: { id: id },
    });
    console.log(`Toggled status for user ID: ${id}`);
  };

  const filteredUsers = useMemo(() => {
    if (!searchTerm) return users;
    const term = searchTerm.toLowerCase();
    return users.filter((user) =>
      [user.first_name, user.other_names, user.email, user.phone, user.country]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(term))
    );
  }, [users, searchTerm]);

  if (loading) return <Box p={2}>Loading users...</Box>;
  if (error)
    return (
      <Box p={2} color="error.main">
        Error: {error}
      </Box>
    );

  return (
    <Box>
      <Typography variant="h5" mb={2}>
        User Management
      </Typography>
      <TextField
        fullWidth
        label="Search Users"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
      />

      {filteredUsers.length === 0 ? (
        <Typography>No users found matching your search criteria.</Typography>
      ) : isMobile ? (
        <Grid container spacing={2}>
          {filteredUsers.map((user) => (
            <Grid item xs={12} key={user.id}>
              <Paper sx={{ pl: 4, pr: 2, pt: 2, pb: 2 }} elevation={2}>
                <Typography variant="subtitle1" align="left" fontWeight="bold">
                  <PersonIcon sx={iconSx} />
                  {capitalize(user.first_name)} {capitalize(user.other_names)}
                </Typography>
                <Typography variant="body2" align="left">
                  <EmailIcon sx={iconSx} />
                  {user.email}
                </Typography>
                <Typography variant="body2" align="left">
                  <PhoneIcon sx={iconSx} />
                  {user.phone}
                </Typography>
                <Typography variant="body2" align="left">
                  <PublicIcon sx={iconSx} />
                  {user.country}
                </Typography>
                <Typography variant="body2" align="left">
                  <PaymentIcon sx={iconSx} />
                  {user.subscription_paid === "yes" ? "Paid" : "Unpaid"}
                </Typography>
                <Typography variant="body2" align="left">
                  <CalendarTodayIcon sx={iconSx} />
                  {user.subscription_days} days left
                </Typography>
                <Box mt={1} display="flex" alignItems="center">
                  <Switch
                    checked={user.isActive}
                    onChange={() => handleToggleActive(user.id)}
                    color="primary"
                  />
                  <Typography variant="body2">
                    {user.isActive ? "Active" : "Inactive"}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper sx={{ pl: 2 }}>
          <Box
            component="table"
            width="100%"
            sx={{ borderCollapse: "collapse" }}
          >
            <Box component="thead">
              <Box component="tr">
                <Box component="th" p={1} align="left">
                  <PersonIcon sx={iconSx} />
                  Name
                </Box>
                <Box component="th" p={1} align="left">
                  <EmailIcon sx={iconSx} />
                  Email
                </Box>
                <Box component="th" p={1} align="left">
                  <PhoneIcon sx={iconSx} />
                  Phone
                </Box>
                <Box component="th" p={1} align="left">
                  <PublicIcon sx={iconSx} />
                  Country
                </Box>
                <Box component="th" p={1} align="left">
                  <PaymentIcon sx={iconSx} />
                  Payment
                </Box>
                <Box component="th" p={1} align="left">
                  <CalendarTodayIcon sx={iconSx} />
                  Days Left
                </Box>
                <Box component="th" p={1} align="left">
                  <ToggleOnIcon sx={iconSx} />
                  Status
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {filteredUsers.map((user, index) => (
                <Box
                  component="tr"
                  key={user.id}
                  sx={{
                    backgroundColor:
                      index % 2 === 0 ? "grey.100" : "transparent",
                  }}
                >
                  <Box component="td" p={1} align="left">
                    <Typography fontWeight="bold">
                      {capitalize(user.first_name)}{" "}
                      {capitalize(user.other_names)}
                    </Typography>
                  </Box>
                  <Box component="td" p={1} align="left">
                    {user.email}
                  </Box>
                  <Box component="td" p={1} align="left">
                    {user.phone}
                  </Box>
                  <Box component="td" p={1} align="left">
                    {user.country}
                  </Box>
                  <Box component="td" p={1} align="left">
                    {user.subscription_paid === "yes" ? "Paid" : "Unpaid"}
                  </Box>
                  <Box component="td" p={1} align="left">
                    {user.subscription_days}
                  </Box>
                  <Box component="td" p={1} align="left">
                    <Switch
                      checked={user.isActive}
                      onChange={() => handleToggleActive(user.id)}
                      color="primary"
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      {filteredUsers.length === 0 && searchTerm && (
        <Typography align="center" mt={3}>
          No users match "{searchTerm}".
        </Typography>
      )}
    </Box>
  );
};

export default UsersList;
